import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MembershipSEO from "./components/MembershipSEO";

type Plan = "basic" | "gold";
type PayMethod = "card" | "paypal";

const plans = {
  basic: {
    name: "Basic Member",
    price: 5,
    period: "per month",
    color: "border-stone-300",
    badge: "bg-stone-100 text-stone-700",
    btnColor: "bg-stone-700 hover:bg-stone-800",
    icon: "ri-shield-line",
    features: [
      "Post job listings (up to 3/month)",
      "Post advertisements (up to 3/month)",
      "View buyer contact details",
      "List factory products (up to 5)",
      "Basic seller profile page",
      "Email support",
    ],
    notIncluded: [
      "Priority listing placement",
      "Gold verified badge",
      "Unlimited posts",
      "Analytics dashboard",
    ],
  },
  gold: {
    name: "Gold Member",
    price: 18,
    period: "per month",
    color: "border-amber-400",
    badge: "bg-amber-100 text-amber-700",
    btnColor: "bg-amber-500 hover:bg-amber-600",
    icon: "ri-vip-crown-fill",
    features: [
      "Unlimited job listings",
      "Unlimited advertisements",
      "View all buyer contact details",
      "Unlimited factory product listings",
      "Priority placement in search results",
      "Gold verified badge on profile",
      "Analytics & performance dashboard",
      "Dropshipping partner access",
      "Social media star commission program",
      "24/7 priority support",
    ],
    notIncluded: [],
  },
};

function PaymentForm({ plan, onSuccess, onBack }: { plan: Plan; onSuccess: () => void; onBack: () => void }) {
  const [payMethod, setPayMethod] = useState<PayMethod>("card");
  const [submitting, setSubmitting] = useState(false);
  const [cardNum, setCardNum] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const selected = plans[plan];

  const formatCard = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));
    try {
      await fetch("https://readdy.ai/api/form/d7ciu69noju8nk5a0skg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      onSuccess();
    } catch {
      onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8">
      {/* Order Summary */}
      <div className={`rounded-xl border-2 ${selected.color} p-4 mb-6 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-full ${plan === "gold" ? "bg-amber-100" : "bg-stone-100"}`}>
            <i className={`${selected.icon} ${plan === "gold" ? "text-amber-500" : "text-stone-600"} text-lg`}></i>
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm">{selected.name}</p>
            <p className="text-xs text-stone-500">Monthly subscription · Cancel anytime</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-bold text-gray-900 text-xl">${selected.price}<span className="text-sm font-normal text-stone-400">/mo</span></p>
          <button onClick={onBack} className="text-xs text-amber-600 hover:underline cursor-pointer">Change plan</button>
        </div>
      </div>

      {/* Payment Method Tabs */}
      <p className="text-sm font-bold text-gray-800 mb-3">Payment Method</p>
      <div className="flex gap-3 mb-6">
        <button
          type="button"
          onClick={() => setPayMethod("card")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all cursor-pointer ${payMethod === "card" ? "border-amber-400 bg-amber-50" : "border-stone-200 hover:border-stone-300"}`}
        >
          <i className="ri-bank-card-line text-lg text-stone-600"></i>
          <span className="text-sm font-semibold text-gray-800">Credit / Debit Card</span>
        </button>
        <button
          type="button"
          onClick={() => setPayMethod("paypal")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all cursor-pointer ${payMethod === "paypal" ? "border-amber-400 bg-amber-50" : "border-stone-200 hover:border-stone-300"}`}
        >
          <i className="ri-paypal-line text-lg text-stone-600"></i>
          <span className="text-sm font-semibold text-gray-800">PayPal</span>
        </button>
      </div>

      <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
        <input type="hidden" name="plan" value={selected.name} />
        <input type="hidden" name="price" value={`$${selected.price}/month`} />
        <input type="hidden" name="payment_method" value={payMethod} />

        {/* Personal Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-stone-600 block mb-1.5">Full Name *</label>
            <input name="full_name" required className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" placeholder="John Smith" />
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-600 block mb-1.5">Email Address *</label>
            <input name="email" type="email" required className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" placeholder="john@example.com" />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-stone-600 block mb-1.5">Phone / WhatsApp</label>
          <input name="phone" className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" placeholder="+1 234 567 8900" />
        </div>

        <div>
          <label className="text-xs font-semibold text-stone-600 block mb-1.5">Business / Company Name</label>
          <input name="company" className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" placeholder="Your company name (optional)" />
        </div>

        {/* Card Fields */}
        {payMethod === "card" && (
          <>
            <div>
              <label className="text-xs font-semibold text-stone-600 block mb-1.5">Card Number *</label>
              <div className="relative">
                <input
                  name="card_number"
                  required
                  value={cardNum}
                  onChange={(e) => setCardNum(formatCard(e.target.value))}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 pr-24"
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <div className="w-7 h-5 bg-stone-100 rounded flex items-center justify-center">
                    <i className="ri-visa-line text-stone-500 text-xs"></i>
                  </div>
                  <div className="w-7 h-5 bg-stone-100 rounded flex items-center justify-center">
                    <i className="ri-mastercard-line text-stone-500 text-xs"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-stone-600 block mb-1.5">Expiry Date *</label>
                <input
                  name="expiry"
                  required
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                  placeholder="MM/YY"
                  maxLength={5}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 block mb-1.5">CVV *</label>
                <input
                  name="cvv"
                  required
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                  placeholder="123"
                  maxLength={4}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-600 block mb-1.5">Name on Card *</label>
              <input name="card_name" required className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" placeholder="As shown on card" />
            </div>
          </>
        )}

        {/* PayPal */}
        {payMethod === "paypal" && (
          <div className="bg-stone-50 rounded-xl p-5 text-center border border-stone-200">
            <i className="ri-paypal-line text-4xl text-stone-500 mb-2 block"></i>
            <p className="text-sm font-semibold text-gray-800 mb-1">Pay with PayPal</p>
            <p className="text-xs text-stone-500 mb-3">You will be redirected to PayPal to complete your payment securely.</p>
            <div>
              <label className="text-xs font-semibold text-stone-600 block mb-1.5">PayPal Email *</label>
              <input name="paypal_email" type="email" required className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 text-center" placeholder="your-paypal@email.com" />
            </div>
          </div>
        )}

        {/* Security Note */}
        <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2.5">
          <i className="ri-shield-check-fill text-green-500 text-base flex-shrink-0"></i>
          <p className="text-xs text-green-700">Your payment is secured with 256-bit SSL encryption. We never store your card details.</p>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className={`w-full py-3.5 ${selected.btnColor} disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2`}
        >
          {submitting ? (
            <><i className="ri-loader-4-line animate-spin"></i>Processing...</>
          ) : (
            <><i className={selected.icon}></i>Subscribe — ${selected.price}/month</>
          )}
        </button>
        <p className="text-xs text-stone-400 text-center">Cancel anytime · No hidden fees · Instant activation</p>
      </form>
    </div>
  );
}

function SuccessScreen({ plan }: { plan: Plan }) {
  const selected = plans[plan];
  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-10 text-center">
      <div className={`w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-5 ${plan === "gold" ? "bg-amber-100" : "bg-green-100"}`}>
        <i className={`${plan === "gold" ? "ri-vip-crown-fill text-amber-500" : "ri-check-line text-green-500"} text-4xl`}></i>
      </div>
      <h3 className="font-bold text-gray-900 text-2xl mb-2">Welcome to {selected.name}!</h3>
      <p className="text-stone-500 text-sm mb-6">Your membership is now active. You have full access to all {selected.name} features.</p>
      <div className="bg-stone-50 rounded-xl p-4 mb-6 text-left space-y-2">
        {selected.features.slice(0, 4).map((f) => (
          <div key={f} className="flex items-center gap-2 text-sm text-gray-700">
            <i className="ri-check-line text-green-500 flex-shrink-0"></i>{f}
          </div>
        ))}
      </div>
      <a href="/" className="inline-block px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap">
        Go to Homepage
      </a>
    </div>
  );
}

export default function MembershipPage() {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [success, setSuccess] = useState(false);

  return (
    <div className="min-h-screen bg-stone-50">
      <MembershipSEO />
      <Navbar />
      <main className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-100 rounded-full px-4 py-1.5 mb-4">
              <i className="ri-vip-crown-fill text-amber-500 text-sm"></i>
              <span className="text-amber-700 text-sm font-semibold">Crown Membership Plans</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Choose Your Membership</h1>
            <p className="text-stone-500 text-base max-w-xl mx-auto">
              Factories, sellers, advertisers &amp; job posters need a paid membership. Buyers always post for <span className="text-green-600 font-semibold">FREE</span>.
            </p>
          </div>

          {!selectedPlan ? (
            <>
              {/* Plan Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
                {(["basic", "gold"] as Plan[]).map((key) => {
                  const p = plans[key];
                  return (
                    <div
                      key={key}
                      className={`relative bg-white rounded-2xl border-2 ${p.color} p-7 flex flex-col ${key === "gold" ? "ring-2 ring-amber-300 ring-offset-2" : ""}`}
                    >
                      {key === "gold" && (
                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                          Most Popular
                        </div>
                      )}
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-12 h-12 flex items-center justify-center rounded-full ${key === "gold" ? "bg-amber-100" : "bg-stone-100"}`}>
                          <i className={`${p.icon} ${key === "gold" ? "text-amber-500" : "text-stone-600"} text-xl`}></i>
                        </div>
                        <div>
                          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${p.badge}`}>{p.name}</span>
                          <div className="flex items-baseline gap-1 mt-1">
                            <span className="text-3xl font-bold text-gray-900">${p.price}</span>
                            <span className="text-stone-400 text-sm">/month</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2.5 mb-6 flex-1">
                        {p.features.map((f) => (
                          <div key={f} className="flex items-start gap-2.5">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-green-500 text-sm"></i>
                            </div>
                            <span className="text-sm text-gray-700">{f}</span>
                          </div>
                        ))}
                        {p.notIncluded.map((f) => (
                          <div key={f} className="flex items-start gap-2.5 opacity-40">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-close-line text-stone-400 text-sm"></i>
                            </div>
                            <span className="text-sm text-stone-400 line-through">{f}</span>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => setSelectedPlan(key)}
                        className={`w-full py-3 ${p.btnColor} text-white font-bold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2`}
                      >
                        <i className={p.icon}></i>
                        Get {p.name} — ${p.price}/mo
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Payment Methods Banner */}
              <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-stone-100 p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-1">Accepted Payment Methods</p>
                  <p className="text-xs text-stone-500">Secure payments via credit card, debit card, or PayPal</p>
                </div>
                <div className="flex items-center gap-4">
                  {[
                    { icon: "ri-visa-line", label: "Visa" },
                    { icon: "ri-mastercard-line", label: "Mastercard" },
                    { icon: "ri-bank-card-line", label: "Debit Card" },
                    { icon: "ri-paypal-line", label: "PayPal" },
                  ].map((m) => (
                    <div key={m.label} className="flex flex-col items-center gap-1">
                      <div className="w-10 h-7 flex items-center justify-center bg-stone-100 rounded-lg">
                        <i className={`${m.icon} text-stone-600 text-base`}></i>
                      </div>
                      <span className="text-xs text-stone-400">{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div className="max-w-3xl mx-auto mt-10">
                <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Frequently Asked Questions</h3>
                <div className="space-y-3">
                  {[
                    { q: "Can I cancel anytime?", a: "Yes, you can cancel your membership at any time. Your access continues until the end of the billing period." },
                    { q: "Do buyers need to pay?", a: "No! Buyers always post their purchasing requirements completely FREE. Only sellers, factories, advertisers, and job posters need a membership." },
                    { q: "What's the difference between Basic and Gold?", a: "Basic ($5/mo) gives you limited posts and access to buyer contacts. Gold ($18/mo) gives you unlimited posts, priority placement, verified badge, analytics, and all premium features." },
                    { q: "Is my payment secure?", a: "Yes. All payments are processed with 256-bit SSL encryption. We never store your card details on our servers." },
                    { q: "Can I upgrade from Basic to Gold?", a: "Yes, you can upgrade at any time. You'll only be charged the difference for the remaining billing period." },
                  ].map((item) => (
                    <div key={item.q} className="bg-white rounded-xl border border-stone-100 p-4">
                      <p className="text-sm font-bold text-gray-900 mb-1.5 flex items-center gap-2">
                        <i className="ri-question-line text-amber-500"></i>{item.q}
                      </p>
                      <p className="text-xs text-stone-500 leading-relaxed pl-5">{item.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : success ? (
            <div className="max-w-lg mx-auto">
              <SuccessScreen plan={selectedPlan} />
            </div>
          ) : (
            <div className="max-w-lg mx-auto">
              <PaymentForm plan={selectedPlan} onSuccess={() => setSuccess(true)} onBack={() => setSelectedPlan(null)} />
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
